import {
  BOTTOM,
  LEFT,
  MIDDLE,
  RIGHT,
  TOP,
  TOP_MIDDLE,
} from '../../../utils/spds/constants/tooltip';
import {
  COMPONENT_STATE_ARGS,
  MENU_ARGS,
  Metadata,
  TOOLTIP_ARGS,
  pick,
  pickAll,
} from '../../../utils/spds/metadata';
import { ROLE_TOOLTIP } from '../../../utils/spds/constants/aria';
import { htmlSafe } from '@ember/template';
import { or } from 'ember-truth-helpers';
import { registerDestructor } from '@ember/destroyable';
import { tracked } from '@glimmer/tracking';
import FlexColumn from '../layout/flex/column.gjs';
import FlexRow from '../layout/flex/row.gjs';
import Super from '../core/base.gjs';
import styles from './tooltip.module.scss';

const METADATA = Metadata.define({
  description: `
Small text box used to display additional information upon hover

### Usage

There are two ways to render a tooltip:

#### Via the tooltip service (recommended)
Pass \`@tooltipContent\` to any component extending \`Spds::Core::Base\`. The base class
automatically registers the tooltip through the \`spds/tooltip\` service, and the
\`Spds::Feedback::Tooltip::Container\` renders it in the top layer. No direct interaction
with the tooltip component is required.

\`\`\`handlebars
<Spds::Action::Button::Primary @tooltipContent="Save your changes">
  Save
</Spds::Action::Button::Primary>
\`\`\`

#### Direct template usage
Include the \`Spds::Feedback::Tooltip\` component directly in your template and pass it
a reference to the anchor element via \`@anchorElement\`. The tooltip will automatically
bind hover listeners to the anchor and manage its own show/hide lifecycle.

\`\`\`handlebars
<Spds::Feedback::Tooltip
  @anchorElement={{this.myElement}}
  @content="Additional information"
  @placement="bottom-middle"
  @title="Save your changes"
/>
\`\`\`

#### Content standards

Tooltips should contain **only text**. Any HTML included should be for formatting purposes only. Interactive content is **not supported**. See our design guidelines for more information.

#### Custom styling

Custom styling can be applied by using the HTML \`class\` attribute **in the direct template usage only**. Usage through the tooltip service does not support custom styling.

### Accessibility

* The trigger element receives \`aria-describedby\` linking it to the tooltip
* Tooltip appears on both hover and keyboard focus of the trigger
* Dismissible via Escape key (handled by the Popover API)
* Cannot contain interactive elements (links, inputs, buttons)
  `,
  extends: [
    pick(COMPONENT_STATE_ARGS, ['id']),
    pick(MENU_ARGS, [
      'anchorElement',
      { description: 'Element to position the tooltip relative to' },
    ]),
    pickAll(TOOLTIP_ARGS, {
      overrides: {
        tooltipContent: { initialValue: 'This is a tooltip', rename: 'content', required: true },
        tooltipOffset: { default: '8px', rename: 'offset' },
        tooltipPlacement: { default: TOP_MIDDLE, rename: 'placement' },
        tooltipTitle: { rename: 'title' },
      },
    }),
  ],
  name: 'Tooltip',
  omits: ['tooltipContent', 'tooltipOffset', 'tooltipPlacement', 'tooltipTitle'],
  resources: {
    figma:
      'https://www.figma.com/design/g4zf0Vkj39VcCyleokv2rl/%E2%9D%96-Air-web-components?node-id=24273-64484&m=dev',
    zeroheight: 'https://www.simplepractice.design/1d0abbc0a/p/89757c-tooltip',
  },
});

export default class SpdsFeedbackTooltip extends Super {
  static metadata = this.setMetadata(METADATA, import.meta.url);

  baseClass = 'spds-feedback-tooltip';
  classesToMap = ['isVisible', 'placement'];
  styles = styles;

  @tracked isVisible = false;
  @tracked _overriddenPlacement = null;

  get anchor() {
    return this.args.anchorElement;
  }

  get anchorId() {
    return this.anchor?.id;
  }

  get anchorBoundingRect() {
    return this.anchor?.getBoundingClientRect();
  }

  get content() {
    return htmlSafe(this.args.content);
  }

  get contentId() {
    return this.composeSubcomponentId('content');
  }

  get id() {
    if (this.anchorId) return `${this.anchorId}-tooltip`;

    return super.id;
  }

  get offset() {
    return this.argOrDefault('offset');
  }

  get placement() {
    return this._overriddenPlacement ?? this.argOrDefault('placement');
  }

  get titleId() {
    return this.composeSubcomponentId('title');
  }

  get tooltipBoundingRect() {
    return this.baseElement?.getBoundingClientRect();
  }

  // NOTE: The positioning methods below (#calculateTop through #resolvePlacement)
  // are intentionally decoupled from the tooltip's DOM and lifecycle so they can
  // be extracted into a shared positioning utility when the core/menu/base component is
  // converted to the Popover API.

  /**
   * Computes the horizontal position. For left/right primaries, the tooltip is
   * placed adjacent to the anchor. For top/bottom primaries, the secondary
   * alignment controls horizontal positioning relative to the anchor.
   *
   * @param {string} primary - "top", "bottom", "left", or "right"
   * @param {string} secondary - "left", "middle", or "right"
   * @returns {number} left position in px
   */
  #calculateLeft(primary, secondary) {
    let { anchorBoundingRect, tooltipBoundingRect } = this;
    let offsetPx = parseInt(this.offset, 10);

    if (primary === LEFT) return anchorBoundingRect.left - tooltipBoundingRect.width - offsetPx;
    if (primary === RIGHT) return anchorBoundingRect.right + offsetPx;

    switch (secondary) {
      case LEFT:
        return anchorBoundingRect.left;
      case MIDDLE:
        return anchorBoundingRect.left + (anchorBoundingRect.width - tooltipBoundingRect.width) / 2;
      case RIGHT:
        return anchorBoundingRect.right - tooltipBoundingRect.width;
    }
  }

  /**
   * Computes absolute {top, left} coordinates by composing the primary
   * and secondary axis calculations.
   *
   * @param {string} placement - e.g. "top-middle", "bottom-left", "right"
   * @returns {{ top: number, left: number }}
   */
  #calculatePosition(placement) {
    let [primary, secondary = MIDDLE] = placement.split('-');

    return {
      top: this.#calculateTop(primary),
      left: this.#calculateLeft(primary, secondary),
    };
  }

  /**
   * Computes the vertical position based on the primary placement direction.
   * For left/right primaries, the tooltip is vertically centered on the anchor.
   *
   * @param {string} primary - "top", "bottom", "left", or "right"
   * @returns {number} top position in px
   */
  #calculateTop(primary) {
    let { anchorBoundingRect, tooltipBoundingRect } = this;
    let offsetPx = parseInt(this.offset, 10);

    switch (primary) {
      case BOTTOM:
        return anchorBoundingRect.bottom + offsetPx;
      case TOP:
        return anchorBoundingRect.top - tooltipBoundingRect.height - offsetPx;
      case LEFT:
      case RIGHT:
        return (
          anchorBoundingRect.top + (anchorBoundingRect.height - tooltipBoundingRect.height) / 2
        );
    }
  }

  /**
   * For top/bottom placements, cascades through alternative secondary positions
   * when the tooltip overflows horizontally. Tries the least disruptive shift
   * first (middle), then the maximum correction (right for right-overflow,
   * left for left-overflow).
   *
   * Only applies to top/bottom primaries since left/right primaries have no
   * secondary axis.
   *
   * @param {string} placement - current placement string (may already be primary-flipped)
   * @param {{ left: boolean, right: boolean }} overflow
   * @returns {string|null} adjusted placement string, or null if no cascade needed
   */
  #cascadeSecondaryIfOverflowing(placement, overflow) {
    let [primary, secondary = MIDDLE] = placement.split('-');

    if (![TOP, BOTTOM].includes(primary)) return null;
    if (!overflow.right && !overflow.left) return null;

    let cascade = [MIDDLE, overflow.right ? RIGHT : LEFT];

    for (let candidate of cascade) {
      if (candidate === secondary) continue;

      let candidatePlacement = `${primary}-${candidate}`;
      let { left: candidateLeft } = this.#calculatePosition(candidatePlacement);
      let candidateOverflow = this.#detectOverflow(0, candidateLeft);

      if (!candidateOverflow.left && !candidateOverflow.right) return candidatePlacement;
    }

    return null;
  }

  /**
   * Tests whether a computed position would overflow the viewport on any edge.
   *
   * @param {number} top - computed top position in px
   * @param {number} left - computed left position in px
   * @returns {{ top: boolean, bottom: boolean, left: boolean, right: boolean }}
   */
  #detectOverflow(top, left) {
    let { height, width } = this.tooltipBoundingRect;

    return {
      top: top < 0,
      bottom: top + height > window.innerHeight,
      left: left < 0,
      right: left + width > window.innerWidth,
    };
  }

  /**
   * Checks whether the tooltip overflows the viewport along its primary axis
   * and returns a placement with the primary direction flipped (e.g. top→bottom).
   * Secondary alignment is preserved when flipping top/bottom placements.
   *
   * @param {string} placement - current placement string
   * @param {{ top: boolean, bottom: boolean, left: boolean, right: boolean }} overflow
   * @returns {string|null} flipped placement string, or null if no flip needed
   */
  #flipIfOverflowing(placement, overflow) {
    let [primary] = placement.split('-');

    if (primary === TOP && overflow.top) return placement.replace(TOP, BOTTOM);
    if (primary === BOTTOM && overflow.bottom) return placement.replace(BOTTOM, TOP);
    if (primary === LEFT && overflow.left) return RIGHT;
    if (primary === RIGHT && overflow.right) return LEFT;

    return null;
  }

  /**
   * Determines the best placement by applying overflow corrections to the
   * requested placement. First flips the primary axis if needed, then
   * cascades through secondary positions for horizontal fit.
   *
   * @returns {string} the resolved placement string
   */
  #resolvePlacement() {
    let placement = this.argOrDefault('placement');
    let { top, left } = this.#calculatePosition(placement);
    let overflow = this.#detectOverflow(top, left);

    let flippedPlacement = this.#flipIfOverflowing(placement, overflow);

    if (flippedPlacement) {
      placement = flippedPlacement;
      ({ top, left } = this.#calculatePosition(placement));
      overflow = this.#detectOverflow(top, left);
    }

    return this.#cascadeSecondaryIfOverflowing(placement, overflow) ?? placement;
  }

  /**
   * Resolves the best-fit placement, applies the computed coordinates to
   * the element, and sets `_overriddenPlacement` for CSS class mapping.
   */
  #positionTooltip() {
    let { anchorBoundingRect, baseElement } = this;

    if (!anchorBoundingRect || !baseElement) return;

    let originalPlacement = this.argOrDefault('placement');
    let placement = this.#resolvePlacement();
    let { top, left } = this.#calculatePosition(placement);

    baseElement.style.top = `${top}px`;
    baseElement.style.left = `${left}px`;
    this._overriddenPlacement = placement !== originalPlacement ? placement : null;
  }

  /**
   * Binds hover and focus listeners to the anchor element for show/hide,
   * sets aria-describedby for accessibility, and ensures non-interactive
   * anchors (e.g. <div>) receive tabindex="0" for keyboard access.
   * All bindings are cleaned up via registerDestructor.
   */
  #setupEventsOnAnchor() {
    let { anchor } = this;
    let madeAnchorFocusable = false;

    anchor.setAttribute('aria-describedby', this.id);

    if (anchor.tabIndex < 0 && !anchor.hasAttribute('tabindex')) {
      anchor.setAttribute('tabindex', '0');
      madeAnchorFocusable = true;
    }

    anchor.addEventListener('mouseenter', this.showHandler);
    anchor.addEventListener('mouseleave', this.hideHandler);
    anchor.addEventListener('focusin', this.showHandler);
    anchor.addEventListener('focusout', this.hideHandler);

    registerDestructor(this, () => {
      anchor.removeAttribute('aria-describedby');

      if (madeAnchorFocusable) anchor.removeAttribute('tabindex');

      anchor.removeEventListener('mouseenter', this.showHandler);
      anchor.removeEventListener('mouseleave', this.hideHandler);
      anchor.removeEventListener('focusin', this.showHandler);
      anchor.removeEventListener('focusout', this.hideHandler);
    });
  }

  /**
   * Listens for the Popover API's "toggle" event so the tooltip hides
   * when dismissed externally (e.g. Escape key, light-dismiss).
   */
  #setupPopoverListeners() {
    this.baseElement.addEventListener('toggle', this.#onPopoverToggle);

    registerDestructor(this, () => {
      this.baseElement.removeEventListener('toggle', this.#onPopoverToggle);
    });
  }

  #onPopoverToggle = event => {
    if (event.newState === 'closed') this.hideHandler();
  };

  setup() {
    let { anchor } = this;

    this.#setupPopoverListeners();

    if (!anchor) return;

    this.#setupEventsOnAnchor();
  }

  /** Hides the tooltip, resets placement override, and removes the scroll listener. */
  hideHandler = () => {
    if (!this.isVisible) return;

    this.isVisible = false;
    this._overriddenPlacement = null;
    this.baseElement.hidePopover();
    window.removeEventListener('scroll', this.hideHandler, { capture: true });
  };

  /** Shows the tooltip via the Popover API, positions it, and listens for scroll to auto-hide. */
  showHandler = () => {
    this.baseElement.showPopover();
    this.#positionTooltip();
    this.isVisible = true;
    window.addEventListener('scroll', this.hideHandler, { capture: true, passive: true });
  };

  <template>
    <FlexColumn
      @fixed={{true}}
      @id={{this.id}}
      @tag="span"
      class={{this.classes}}
      data-test-spds-feedback-tooltip
      popover="auto"
      role={{ROLE_TOOLTIP}}
      {{this.setupComponent}}
      {{this.validateArguments}}
      ...attributes
    >
      {{#if (or @title (has-block "title"))}}
        <FlexRow
          @id={{this.titleId}}
          @tag="span"
          class="spds-feedback-tooltip-title"
          data-test-spds-feedback-tooltip-title={{this.titleId}}
        >
          {{#if (has-block "title")}}
            {{yield to="title"}}
          {{else}}
            {{@title}}
          {{/if}}
        </FlexRow>
      {{/if}}
      {{#if (or @content (has-block "content"))}}
        <FlexRow
          @id={{this.contentId}}
          @tag="span"
          class="spds-feedback-tooltip-content"
          data-test-spds-feedback-tooltip-content={{this.contentId}}
        >
          {{#if (has-block "content")}}
            {{yield to="content"}}
          {{else}}
            {{this.content}}
          {{/if}}
        </FlexRow>
      {{/if}}
    </FlexColumn>
  </template>
}

export const TOOLTIP_METADATA = SpdsFeedbackTooltip.metadata;
